import React, {useMemo, useCallback} from "react";
import {useStore} from "hooks/useStore";
import {useHistory, useLocation} from "react-router-dom";
import {PaymentsFilters} from "./components/PaymentsFilters";
import {PaymentsTable} from "./components/PaymentsTable";
import {MakePayment} from "./components/MakePayment";
import {PaymentsPageContainer} from "./styles/PaymentsPage.styles";
import {useFetch} from "hooks/useFetch";
import {isUserIdInList} from "utils/helpers";
import {
    parsePaymentsFiltersFromSearch,
    buildListRequestParams,
    buildExcelRequestParams,
    appendPaymentsParamsToUrl,
    parseExcelExportResponse
} from "./paymentsListParams";
import {Button, message} from "antd";
import {DownloadOutlined} from "@ant-design/icons";
import moment from "moment";

const PAYMENTS_EXCEL_MSG_KEY = "payments-excel-export";

export const PaymentsPage = () => {
    const history = useHistory();
    const location = useLocation();
    const stores = useStore();

    const filterValues = useMemo(() => parsePaymentsFiltersFromSearch(location.search), [
        location.search
    ]);

    const [zoneManagementLoading, zoneManagementData] = useFetch({
        defaultDataValue: {data: []},
        fetchFn: stores.operationOrganizationStore.getZoneManagementList.bind(null, {
            paginate: false,
            includeDeleted: false
        }),
        dependencies: [],
        initialCall: true
    });

    const [zoneLoading, zoneData] = useFetch({
        defaultDataValue: {data: []},
        fetchFn: stores.operationOrganizationStore.getZoneList.bind(null, {
            paginate: false,
            includeDeleted: false,
            zoneManagementId: filterValues?.zoneManagement
        }),
        dependencies: [filterValues?.zoneManagement],
        initialCall: true
    });

    const updateUrl = useCallback(
        next => {
            const parts = [];
            appendPaymentsParamsToUrl(parts, next);
            const search = parts.length ? `?${parts.join("&")}` : "";
            if (location.search !== search) {
                history.replace({search});
            }
        },
        [history, location.search]
    );

    const listParams = useMemo(() => buildListRequestParams(filterValues), [filterValues]);

    const fetchListData = useCallback(() => {
        return stores.courierWithdrawRequestStore.getCourierWithdrawRequestList(listParams);
    }, [listParams, stores.courierWithdrawRequestStore]);

    const [loading, responseData, , , refetch] = useFetch({
        defaultDataValue: {data: [], totalAmount: 0},
        fetchFn: fetchListData,
        dependencies: [location.search],
        initialCall: true
    });

    const isAgency = stores.meStore.checkRole("isAgency");

    const showPaymentActions = useMemo(
        () =>
            !isAgency &&
            isUserIdInList(stores.meStore.userId, responseData?.paymentAuthorizedUsers),
        [isAgency, stores.meStore.userId, responseData?.paymentAuthorizedUsers]
    );

    const downloadPaymentsExcel = useCallback(async () => {
        const params = buildExcelRequestParams(filterValues);
        const resultOfRequest = await stores.courierWithdrawRequestStore.requestCourierWithdrawList(
            params
        );

        if (resultOfRequest.status !== 200) {
            let errMsg = "Excel indirilemedi.";
            const d = resultOfRequest.data;
            if (d instanceof Blob) {
                try {
                    const t = await d.text();
                    const j = JSON.parse(t);
                    if (j.message) errMsg = j.message;
                } catch (e) {
                    void e;
                }
            } else if (resultOfRequest.data?.message) {
                errMsg = resultOfRequest.data.message;
            }
            message.destroy(PAYMENTS_EXCEL_MSG_KEY);
            message.error(errMsg);
            return resultOfRequest;
        }

        const {url, revoke} = await parseExcelExportResponse(resultOfRequest.data);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute(
            "download",
            `odeme-kayitlari-${moment().format("YYYY-MM-DD-HHmmss")}.xlsx`
        );
        document.body.appendChild(link);
        link.click();
        link.remove();
        revoke?.();
        message.success({
            content: "Excel dosyası indirildi.",
            key: PAYMENTS_EXCEL_MSG_KEY,
            duration: 3
        });
        return resultOfRequest;
    }, [filterValues, stores.courierWithdrawRequestStore]);

    const [excelLoading, , , , refetchExcelExport] = useFetch({
        fetchFn: downloadPaymentsExcel,
        dependencies: [downloadPaymentsExcel],
        initialCall: false,
        condition: false,
        defaultDataValue: null
    });

    const handleDownloadExcel = useCallback(async () => {
        message.loading({
            content: "Excel hazırlanıyor...",
            key: PAYMENTS_EXCEL_MSG_KEY,
            duration: 0
        });
        try {
            await refetchExcelExport();
        } catch (e) {
            console.error(e);
            message.destroy(PAYMENTS_EXCEL_MSG_KEY);
            message.error(e?.message || "Excel indirilemedi.");
        }
    }, [refetchExcelExport]);

    return (
        <PaymentsPageContainer>
            <PaymentsFilters
                filterValues={filterValues}
                onFilterChange={updateUrl}
                zoneManagementData={zoneManagementData}
                zoneManagementLoading={zoneManagementLoading}
                zoneData={zoneData}
                zoneLoading={zoneLoading}
                toolbarRight={
                    <MakePayment
                        embedded
                        amountPayable={responseData?.totalAmount ?? 0}
                        zoneManagement={filterValues?.zoneManagement}
                        zone={filterValues?.zone}
                        city={filterValues?.city}
                        status={filterValues?.status}
                        searchText={filterValues?.searchText}
                        zoneManagementList={zoneManagementData}
                        zoneList={zoneData}
                        onPaymentSuccess={refetch}
                        showPaymentButton={showPaymentActions}
                        excelButton={
                            isAgency ? null : (
                                <Button
                                    type="primary"
                                    icon={<DownloadOutlined />}
                                    onClick={handleDownloadExcel}
                                    loading={excelLoading}
                                >
                                    Excel Çıktısı İndir
                                </Button>
                            )
                        }
                    />
                }
            />
            <PaymentsTable
                dataSource={responseData?.data ?? []}
                loading={loading}
                refetch={refetch}
                showCancelButton={showPaymentActions}
            />
        </PaymentsPageContainer>
    );
};
